/**
 * FmContextPanel — view-scoped contextual panel (~288px, glass, not solid).
 *
 * Reads activePanel + isPanelOpen from FmContextPanelContext.
 * When isPanelOpen=false: not rendered (panel closed, canvas holds full width).
 * When content=null (no page has registered real content): shows empty state in DEV only.
 * In production, renders nothing when no content is registered (no dev placeholder).
 */
import React from "react";
import { CloseOutlined } from "@ant-design/icons";
import { useLanguage } from "@/i18n";
import {
  useFmContextPanel,
  type PanelEntry,
} from "@/contexts/fmContextPanelInternals";
import "./FmContextPanel.css";
import { fmMapOcclusion } from '@/shared/ui/mapOcclusion';

/** Empty state shown when panel is open but view has no content registered yet. DEV ONLY. */
const PanelEmptyState: React.FC<{ tooltip: string }> = ({ tooltip }) => {
  const { t } = useLanguage();
  return (
    <div
      className="fm-context-panel__empty"
      data-testid="fm-context-panel-empty-state"
    >
      <span className="fm-context-panel__empty-title">
        {tooltip || t.shell.panel.emptyTitle}
      </span>
      <p className="fm-context-panel__empty-body">{t.shell.panel.emptyBody}</p>
    </div>
  );
};

export interface FmContextPanelProps {
  hoverPeek?: boolean;
}

function hoverPeekEvents(
  enabled: boolean,
  cancel: () => void,
  scheduleClose: () => void,
): React.HTMLAttributes<HTMLDivElement> {
  if (!enabled) return {};
  return {
    onMouseEnter: cancel,
    onMouseLeave: scheduleClose,
    onFocus: cancel,
    onBlur: scheduleClose,
  };
}

function resolveEntry(
  panels: PanelEntry[],
  activePanel: string | null,
): PanelEntry | null {
  return (
    (activePanel
      ? panels.find((panel) => panel.key === activePanel)
      : panels[0]) ?? null
  );
}

function resolveBody(
  entry: PanelEntry | null,
  fallbackTitle: string,
): React.ReactNode {
  if (entry?.content) return entry.content;
  if (!import.meta.env.DEV) return null;
  return <PanelEmptyState tooltip={entry?.tooltip ?? fallbackTitle} />;
}

const FmContextPanel: React.FC<FmContextPanelProps> = ({
  hoverPeek = false,
}) => {
  const {
    panels,
    activePanel,
    isPanelOpen,
    closePanel,
    scheduleClosePeekedPanel,
    cancelPeekClose,
  } = useFmContextPanel();
  const { t } = useLanguage();

  if (!isPanelOpen) return null;

  const entry = resolveEntry(panels, activePanel);

  // In production, fully unmount panel (return null) when no content is registered.
  // In DEV, show placeholder to help developers understand what content should be registered.
  if (!entry?.content && !import.meta.env.DEV) return null;

  const body = resolveBody(entry, t.shell.panel.title);
  const hoverEvents = hoverPeekEvents(
    hoverPeek,
    cancelPeekClose,
    scheduleClosePeekedPanel,
  );

  return (
    <div
      id={entry ? `fm-context-panel-${entry.key}` : undefined}
      className="fm-context-panel"
      data-testid="fm-context-panel"
      {...fmMapOcclusion('panel', 'panel')}
      {...hoverEvents}
    >
      <div className="fm-context-panel__header">
        <span
          className="fm-context-panel__title"
          data-testid="fm-context-panel-title"
        >
          {entry?.tooltip ?? t.shell.panel.title}
        </span>
        <button
          type="button"
          className="fm-context-panel__close"
          aria-label={t.shell.panel.closeAriaLabel}
          data-testid="fm-context-panel-close"
          onClick={closePanel}
        >
          <CloseOutlined />
        </button>
      </div>
      <div className="fm-context-panel__body">{body}</div>
    </div>
  );
};

export default FmContextPanel;
